import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';

/**
 * Os sete estados que o DESIGN.md cobra de todo componente interativo:
 * repouso, hover, foco, pressionado, desabilitado, carregando e erro. Ficar
 * com metade não é entregar — e antes deste arquivo faltavam três.
 *
 * O foco vem do `:focus-visible` global do `theme.css`, com `outline-offset`,
 * e por isso não é redeclarado aqui.
 */
/**
 * As cinco variantes, da mais alta para a mais baixa — e a ordem é a leitura.
 *
 * | variante     | para que                                      | como se distingue |
 * | ------------ | --------------------------------------------- | ----------------- |
 * | `primario`   | a ação-chave da tela, uma por tela             | preenchido no laranja da marca, rótulo branco, peso 700 |
 * | `secundario` | alternativa que acompanha a primária           | cinza tingido, sem contorno |
 * | `contorno`   | ação de impacto menor, que precisa ser achada  | contorno laranja, sem preenchimento |
 * | `discreto`   | ação terciária, some no fundo até o ponteiro   | nada, até o hover |
 * | `perigo`     | destrói alguma coisa                           | preenchido vermelho, peso 700 |
 *
 * **`contorno` nasceu em 17/09 com o nome do que ele sempre foi**: era o
 * `secundario` — fundo da superfície com contorno —, e o nome ficou livre
 * quando a casa pediu um secundário de cinza tingido. As 51 chamadas que
 * existiam mudaram de nome junto, então nenhuma tela trocou de forma; o que
 * mudou é a cor do contorno e do rótulo, que passaram do neutro para o laranja.
 *
 * Os dois estados que NÃO são variante: `loading` e `disabled`. Eles valem
 * sobre qualquer uma das cinco, e é por isso que não entram nesta lista — um
 * "botão de carregando" obrigaria a escolher qual ação pode carregar.
 */
type Variant = 'primario' | 'secundario' | 'contorno' | 'discreto' | 'perigo';
type Size = 'lg' | 'md' | 'sm';

/*
 * As chaves são os VALORES de `Variant`, não nomes nossos — o mapa é
 * indexado pela união literal, e traduzi-las quebra a indexação.
 */
// @valor
const variants: Record<Variant, string> = {
  /*
   * O primário é `font-bold` e não `font-medium` desde 06/09: no desenho de
   * 05/09 ele é o único peso 700 da barra de ações, e é isso que separa "abrir
   * demanda" de "exportar" antes de a pessoa ler as duas palavras.
   */
  /*
   * `acao-primaria` não pinta nada: é a marca que o `theme.css` procura para
   * inverter este botão quando ele cai DENTRO de um bloco tingido.
   *
   * Sem ela, "Abrir demanda" no cabeçalho do portal ficava laranja sobre
   * laranja — 1,2:1 contra o bloco, ou seja, o contorno do controle desaparece
   * e sobra o rótulo branco flutuando. É o caso da WCAG 1.4.11, o mesmo que o
   * comentário do `secundario` descreve logo abaixo, com o sinal trocado.
   *
   * A marca é uma classe e não um `data-`, porque metade destes botões não é
   * `<button>`: quem navega é `<Link>` com `buttonClasses()`, e um atributo
   * posto aqui não chegaria neles.
   */
  /*
   * **Este botão é a exceção de contraste da casa, e ela é uma decisão.**
   *
   * O preenchimento é o laranja da marca cheio e o rótulo é branco: medido,
   * **2.66:1** em repouso e **2.29:1** em hover, contra um piso de 4.5. A casa
   * escolheu em 18/09/2026, com as duas alternativas que passam já medidas na
   * mesa (rótulo navy sobre o laranja intacto, 5,94:1; rótulo branco sobre o
   * laranja escurecido, 4,92:1) e com o número desta na frente. O registro
   * inteiro está em `--cor-primaria-contraste`, no `theme.css`, e o mapa
   * `WAIVED` de `theme.test.ts` mede o par e reprova se ele PIORAR.
   *
   * **Nada aqui deve sugerir que este botão cumpre AA.** Ele não cumpre.
   *
   * O CONTORNO é o que sobrou de defensável, e por isso ele fica: o
   * preenchimento contra o cartão branco é 2.66:1 e contra a página 2.45:1,
   * abaixo dos 3 que a WCAG 1.4.11 cobra do limite de um controle. Sem ele, o
   * botão não tem borda que se separe do fundo — o mesmo defeito que o
   * comentário do `secundario` descreve, com o sinal trocado. `primaria-texto`
   * é o mesmo laranja escurecido, e ele fecha 5.80:1 ali.
   */
  primario:
    'acao-primaria border border-primaria-texto bg-primaria font-bold text-primaria-contraste hover:bg-primaria-forte active:bg-primaria-forte',
  /*
   * `borda-forte` e não `borda`, ao contrário do desenho.
   *
   * Lá o contorno do botão claro é `--line` (1.22:1 contra a superfície). Aqui
   * ele é a ÚNICA coisa que diz "isto é um botão": o preenchimento é branco
   * sobre uma página quase branca, e sem contorno legível sobra um rótulo
   * solto. É o caso que a WCAG 1.4.11 descreve, e por isso é o único lugar em
   * que este arquivo não copia o desenho.
   */
  /*
   * O cinza tingido de navy, com transparência alta — nunca um cinza próprio.
   *
   * `bg-texto/8` acompanha o tema sozinho: no claro ele lava de branco, no
   * escuro de navy, e o rótulo continua sendo `texto` nos dois. Um token novo
   * de cinza precisaria de dois valores e de um par na régua de contraste para
   * dizer a mesma coisa.
   *
   * **Ele não tem contorno, e isso é a escolha e não o esquecimento.** Contra o
   * cartão branco a pastilha fica em ~1.1:1: quem precisa que a borda do
   * controle se separe do fundo usa `contorno`, que existe para isso. Aqui o
   * que diz "isto é um botão" é o rótulo em peso 600 sobre uma pastilha — é o
   * degrau entre o `contorno` e o `discreto`, e é o que a casa pediu.
   */
  secundario: 'bg-texto/8 font-semibold text-texto hover:bg-texto/12 active:bg-texto/15',
  /*
   * O contorno é laranja, e o rótulo é o laranja um passo mais escuro.
   *
   * Desde 18/09 os dois são o MESMO token, `primaria-texto`: o laranja
   * escurecido, 5.10:1 contra a página e 5.56:1 contra o cartão. Eram
   * `primaria` na borda e `primaria-forte` no rótulo, e os dois nomes passaram a
   * significar outra coisa quando `primaria` virou o `#F77704` cheio — que como
   * contorno fino dá 2.76:1, abaixo do piso da 1.4.11.
   *
   * O que se perde é o degrau de cor entre a borda e o rótulo. O que separa os
   * dois agora é o peso do texto, e o contorno é o que diz que ali há um botão.
   *
   * `acao-contorno` não pinta nada: é a marca que o `theme.css` procura para
   * inverter este botão quando ele cai DENTRO de um bloco tingido, do mesmo
   * jeito que `acao-primaria` faz com o primário. Sem ela, o contorno laranja
   * sobre o cabeçalho laranja do portal é o mesmo 1.2:1 daquele defeito.
   */
  contorno:
    'acao-contorno border border-primaria-texto bg-transparent font-semibold text-primaria-texto hover:bg-primaria-suave active:bg-primaria-suave',
  discreto:
    'bg-transparent font-semibold text-texto-suave hover:bg-superficie-suave hover:text-texto active:bg-superficie-suave active:text-texto',
  // `erro-forte` e não `brightness-95`: filtro não passa por teste de contraste
  // e, no tema escuro, escurecer um vermelho claro anda para o lado errado.
  perigo: 'bg-erro font-bold text-texto-inverso hover:bg-erro-forte active:bg-erro-forte',
};

/**
 * O que todo botão tem, seja qual for a variante ou o tamanho.
 *
 * **`cursor-pointer` é explícito, e a ausência dele era um defeito de toda a
 * casa.** O Tailwind v4 tirou do preflight a regra que dava mãozinha a
 * `<button>`; como o preflight é dele, nada no repositório a repôs, e **nenhum**
 * botão das duas SPAs mudava o ponteiro desde a migração. Ninguém notou porque
 * botão com rótulo e preenchimento já parece clicável — apareceu no gatilho de
 * reticências da tabela, que é um ícone sozinho, sem moldura e sem texto: ali o
 * ponteiro era a única pista de que havia algo para clicar.
 */
const BASE = cn(
  'relative inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-borda',
  'transition-[background-color,color,border-color] active:scale-[0.98]',
  'disabled:pointer-events-none disabled:opacity-50',
);

/*
 * 36px e 30px, que são as duas alturas do desenho de 05/09 — a barra de ações
 * do topo e o controle dentro de tabela. Eram 40px e 32px.
 *
 * Onde há dedo, os dois sobem para os 44px que a WCAG 2.5.5 pede. Num telefone
 * não existe alvo pequeno: o que `sm` continua reduzindo é o texto e o respiro
 * lateral, que é o que dá a hierarquia; a altura do alvo não é enfeite, é a
 * régua. Medido em "Meus acessos", onde cada botão de encerrar sessão dava
 * 40px num Pixel 7.
 */
// @valor
const sizes: Record<Size, string> = {
  /*
   * `lg` (19/10/2026): a ação principal de um formulário do ASSOCIADO — o
   * "Continuar" do acionamento de Periféricos. 48px no computador e 56px onde
   * há dedo, com texto de 16px: quem usa é leigo, muitas vezes mais velho, e
   * no telefone. É o tamanho que Apple (44pt) e Material (48dp) tratam como
   * piso, com folga, e o AAA da WCAG (2.5.5, 44px).
   */
  lg: 'h-12 gap-2 px-5 text-base font-semibold pointer-coarse:h-14',
  md: 'h-9 gap-2 px-3.5 text-[0.8125rem] pointer-coarse:h-11',
  sm: 'h-[1.875rem] gap-1.5 px-2.75 text-xs pointer-coarse:h-11',
};

/**
 * As classes do botão, sem o botão.
 *
 * Existe porque os primitivos do shadcn (`AlertDialogAction`, `DialogClose`)
 * precisam que o elemento seja o deles — o `Action` do Radix é quem fecha o
 * diálogo e devolve o foco. Sem isto, a saída era instalar o `button.tsx` do
 * shadcn ao lado deste arquivo: dois botões, duas escalas de tamanho e duas
 * definições de "primário" no mesmo pacote.
 *
 * Quem escreve tela usa `Button`. Isto é ferramenta de primitivo.
 */
/**
 * A cor do giro, por variante — e ela não é `currentColor` em todas.
 *
 * O `--cor-carregando` (o laranja da marca escurecido até 3:1 sobre fundo
 * claro) vale onde o fundo do botão é claro. Sobre o preenchimento `#F77704` do
 * primário e sobre o vermelho do perigo o giro herda a cor do rótulo, que é a
 * única que aquele fundo garante.
 *
 * Um mapa e não um `if`: uma variante nova não compila sem responder isto.
 */
// @valor
const LOADING: Record<Variant, string> = {
  primario: '',
  perigo: '',
  secundario: 'text-carregando',
  contorno: 'text-carregando',
  discreto: 'text-carregando',
};

export function buttonClasses({
  variant = 'primario',
  size = 'md',
  className,
}: { variant?: Variant; size?: Size; className?: string } = {}): string {
  return cn(BASE, variants[variant], sizes[size], className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Vai antes do rótulo. Decorativo: quem nomeia a ação é o texto. */
  icon?: IconName;
  children?: ReactNode;
}

export function Button({
  variant = 'primario',
  size = 'md',
  loading = false,
  icon,
  className,
  disabled,
  type,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      // Sem isto, todo botão dentro de um <form> é um submit por omissão —
      // inclusive o que só abre um painel.
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClasses({ variant, size, className })}
    >
      {/*
       * O rótulo continua ocupando o lugar enquanto carrega. Trocá-lo por
       * "Aguarde…" mudava a largura do botão no meio do clique e apagava o
       * nome da ação exatamente quando a pessoa quer conferir o que apertou.
       */}
      <span
        className={cn(
          'inline-flex items-center',
          size === 'sm' ? 'gap-1.5' : 'gap-2',
          loading && 'invisible',
        )}
      >
        {icon && <Icon nome={icon} size={size === 'lg' ? 20 : 16} />}
        {children}
      </span>

      {loading && (
        <span
          className={cn('absolute inset-0 grid place-items-center', LOADING[variant])}
        >
          <Spin />
          {/*
           * Quem pediu menos movimento recebe a palavra no lugar do giro: um
           * giro congelado pela regra global de `prefers-reduced-motion` não
           * comunica "carregando", comunica "travou".
           */}
          <span className="hidden motion-reduce:block">Enviando…</span>
          <span className="sr-only motion-reduce:hidden">Enviando…</span>
        </span>
      )}
    </button>
  );
}

/**
 * O giro de carregando — exportado porque o campo de CEP do Endereço (19/09)
 * precisa do mesmo símbolo dentro do próprio campo, não só dentro de um botão.
 */
export function Spin({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="animate-spin motion-reduce:hidden"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2.5"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 00-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
